'use client';

import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type CarouselProps = {
  /** Accessible name of the card list. */
  label: string;
  /** `<li>` cards rendered on the server (fixed width, `snap-start`). */
  children: ReactNode;
  /** Call to action at the end of the control row. */
  action?: ReactNode;
};

type ScrollState = { overflow: boolean; atStart: boolean; atEnd: boolean };

const sameState = (a: ScrollState, b: ScrollState) =>
  a.overflow === b.overflow && a.atStart === b.atStart && a.atEnd === b.atEnd;

/**
 * Figma 4460:1998 + 4468:1163 — horizontally scrolling card row that bleeds to the right edge of
 * the viewport, with arrow buttons and a scroll indicator. Native scrolling (touch, trackpad,
 * keyboard focus) keeps working; the indicator is driven by two CSS variables set on scroll.
 */
export function AssortmentCarousel({ label, children, action }: CarouselProps) {
  const { t } = useTranslations();
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const trackRef = useRef<HTMLSpanElement>(null);
  const [state, setState] = useState<ScrollState>({
    overflow: true,
    atStart: true,
    atEnd: false,
  });

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const { scrollLeft, scrollWidth, clientWidth } = list;
      const max = scrollWidth - clientWidth;
      trackRef.current?.style.setProperty('--thumb-size', String(clientWidth / scrollWidth));
      trackRef.current?.style.setProperty('--thumb-start', String(scrollLeft / scrollWidth));

      const next = { overflow: max > 1, atStart: scrollLeft <= 1, atEnd: scrollLeft >= max - 1 };
      setState(previous => (sameState(previous, next) ? previous : next));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    list.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(list);

    return () => {
      cancelAnimationFrame(frame);
      list.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, []);

  const scroll = (direction: -1 | 1) => {
    const list = listRef.current;
    const card = list?.firstElementChild;
    if (!list || !(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({
      left: direction * (card.offsetWidth + gap),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      {/* Bleeds from the container to the viewport edge; the end padding lets the last card stop
          at the container edge. */}
      <ul
        id={listId}
        ref={listRef}
        aria-label={label}
        className="hide-scroll-bar mr-[calc(50%-50vw)] flex snap-x snap-mandatory gap-3 overflow-x-auto pr-[calc(50vw-50%)] md:gap-6 lg:gap-8"
      >
        {children}
      </ul>

      <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-8">
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-6 md:gap-8',
            !state.overflow && 'hidden',
          )}
        >
          {/* Mobile 4701:4057 shows the scroll indicator only; cards scroll by touch or focus. */}
          <div className="hidden shrink-0 gap-3 md:flex">
            <ArrowButton
              icon="arrow-left"
              label={t('text3525')}
              controls={listId}
              disabled={state.atStart}
              onClick={() => scroll(-1)}
            />
            <ArrowButton
              icon="arrow-right"
              label={t('text3526')}
              controls={listId}
              disabled={state.atEnd}
              onClick={() => scroll(1)}
            />
          </div>
          <span
            ref={trackRef}
            aria-hidden="true"
            className="relative h-1 min-w-0 flex-1 overflow-hidden bg-[#e6e8e2] [--thumb-size:0.43] [--thumb-start:0]"
          >
            <span className="absolute inset-y-0 left-[calc(var(--thumb-start)*100%)] w-[calc(var(--thumb-size)*100%)] bg-[#c9cdc4]" />
          </span>
        </div>
        {action ? <div className="shrink-0 md:ml-auto">{action}</div> : null}
      </div>
    </div>
  );
}

type ArrowButtonProps = {
  icon: 'arrow-left' | 'arrow-right';
  label: string;
  controls: string;
  disabled: boolean;
  onClick: () => void;
};

function ArrowButton({ icon, label, controls, disabled, onClick }: ArrowButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-controls={controls}
      disabled={disabled}
      onClick={onClick}
      className="flex size-[50px] items-center justify-center rounded-[12px] rounded-tr-none bg-[#fbfbf9] text-[#14171a] transition-[color,opacity] hover:text-[#2c7a25] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-2 disabled:cursor-default disabled:text-[#14171a] disabled:opacity-50 motion-reduce:transition-none"
    >
      <Icon name={icon} className="size-5" />
    </button>
  );
}
